import { Link, Navigate, useLocation } from "react-router-dom";
import { can, type Capability } from "@snipe/core";
import type { ReactNode } from "react";
import { useSession } from "./session";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";

function Checking() {
  return (
    <p role="status" className="p-6 text-sm text-muted-foreground">
      Checking your session…
    </p>
  );
}

/** Signed in, or sent to the login form with somewhere to come back to. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useSession();
  const location = useLocation();

  if (loading) return <Checking />;
  if (user === null) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <>{children}</>;
}

/**
 * Gated on a capability from @snipe/core's map, never on `role === "admin"`.
 *
 * The map is the single statement of who may see what, and asking it here
 * rather than comparing a string is what let the `ops` role be removed
 * without touching this file. This is the same predicate the API enforces --
 * the guard only decides what is worth rendering, and a customer who edits
 * their way past it still gets a 403 from every /ops endpoint.
 */
export function RequireCapability({
  capability,
  children,
}: {
  capability: Capability;
  children: ReactNode;
}) {
  const { user, loading } = useSession();
  const location = useLocation();

  if (loading) return <Checking />;
  if (user === null) return <Navigate to="/login" replace state={{ from: location.pathname }} />;

  if (!can(user.role, capability)) {
    return (
      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle>No access</CardTitle>
        </CardHeader>
        <CardBody className="flex flex-col gap-3 text-sm text-muted-foreground">
          <p>
            This page needs <code className="text-foreground">{capability}</code>. You are{" "}
            <span className="text-foreground">{user.email}</span>, a{" "}
            <span className="text-foreground">{user.role}</span> account.
          </p>
          <p>Ask an admin for access, then reload.</p>
          <Link className="text-primary underline underline-offset-4" to="/alerts">
            Go to your alerts
          </Link>
        </CardBody>
      </Card>
    );
  }

  return <>{children}</>;
}
